// Nabídky: kontextové menu a rozbalovací menu. Jedny položky pro ribbon, pravé tlačítko i klávesnici.
import { h, clear, swallowNextClick } from '../dom';
import { icon } from '../icons';

export interface MenuItem {
  id?: string;
  label: string;
  icon?: string | null;
  /** barevná tečka místo ikony (kalendáře, adresáře, štítky) */
  color?: string | null;
  shortcut?: string | null;
  disabled?: boolean;
  danger?: boolean;
  checked?: boolean;
  separator?: boolean;
  /** šedý nadpis oddílu (nedá se vybrat) */
  header?: boolean;
  children?: MenuItem[];
  onSelect?: () => void;
}

let openMenu: IwMenu | null = null;

export class IwMenu extends HTMLElement {
  private items: MenuItem[] = [];
  private cleanup: (() => void) | null = null;
  private sub: IwMenu | null = null;
  private parentMenu: IwMenu | null = null;
  private subTimer: number | undefined;
  private focusIndex = -1;
  private noFocus = false;
  /** odkud se nabídka otevřela (tlačítko / prvek s fokusem): po zavření se tam vrátí fokus, okno ji zavře s sebou */
  private origin: HTMLElement | null = null;

  /**
   * @param opts.focus false = nabídka nebere fokus (našeptávač pod textovým polem; pole ji řídí přes handleKey())
   */
  static show(items: MenuItem[], x: number, y: number, anchor?: HTMLElement, opts: { focus?: boolean } = {}): IwMenu {
    IwMenu.closeAll();
    const m = new IwMenu();
    m.items = items;
    m.noFocus = opts.focus === false;
    const active = document.activeElement instanceof HTMLElement && document.activeElement !== document.body ? document.activeElement : null;
    m.origin = anchor ?? active;
    m.render();
    document.body.appendChild(m);
    m.position(x, y, anchor);
    // klik mimo nabídku (podnabídky jsou samostatné prvky v body, ale patří k ní)
    const listener = (e: PointerEvent) => {
      const target = e.target as Element | null;
      if (target?.closest?.('iw-menu')) return;
      if (anchor && target && anchor.contains(target)) swallowNextClick(anchor); // druhý klik na tlačítko = zavřít
      m.close();
    };
    window.setTimeout(() => document.addEventListener('pointerdown', listener, true), 0);
    m.cleanup = () => document.removeEventListener('pointerdown', listener, true);
    openMenu = m;
    m.setAttribute('role', 'menu');
    m.tabIndex = -1;
    if (!m.noFocus) m.focus();
    return m;
  }

  static showAt(items: MenuItem[], anchor: HTMLElement, opts: { focus?: boolean } = {}): IwMenu {
    const r = anchor.getBoundingClientRect();
    return IwMenu.show(items, r.left, r.bottom + 4, anchor, opts);
  }

  /** Klávesy z cizího prvku (textové pole s našeptávačem): šipky, Enter, Esc. Vrací true, když je zpracovala. */
  handleKey(e: KeyboardEvent): boolean {
    if (e.key === 'Enter' && this.focusIndex < 0) return false; // bez vybrané položky Enter patří poli
    if (!['ArrowDown', 'ArrowUp', 'Enter', 'Escape'].includes(e.key)) return false;
    this.onKey(e);
    return true;
  }

  static closeAll(): void {
    openMenu?.close();
  }

  /** Zavře nabídku, která patří do daného prvku (zavírané okno); nabídky jiných oken nechá. */
  static closeIn(el: HTMLElement): void {
    if (openMenu && (!openMenu.origin || !openMenu.origin.isConnected || el.contains(openMenu.origin))) openMenu.close();
  }

  static isOpen(): boolean {
    return openMenu !== null && openMenu.isConnected;
  }

  private position(x: number, y: number, anchor?: HTMLElement): void {
    const r = this.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let left = x;
    let top = y;
    if (left + r.width > vw - 8) left = anchor ? anchor.getBoundingClientRect().right - r.width : vw - r.width - 8;
    if (top + r.height > vh - 8) top = anchor ? anchor.getBoundingClientRect().top - r.height - 4 : vh - r.height - 8;
    this.style.left = Math.max(4, left) + 'px';
    this.style.top = Math.max(4, top) + 'px';
  }

  private render(): void {
    clear(this);
    this.items.forEach((it, idx) => {
      if (it.separator) {
        this.appendChild(h('div', { class: 'menu-sep', role: 'separator' }));
        return;
      }
      if (it.header) {
        this.appendChild(h('div', { class: 'menu-head', role: 'presentation' }, it.label));
        return;
      }
      const row = h(
        'div',
        { class: 'menu-item' + (it.disabled ? ' disabled' : '') + (it.danger ? ' danger' : ''), role: 'menuitem', dataset: it.id ? { idx: String(idx), id: it.id } : { idx: String(idx) } },
        h('span', { class: 'menu-check' }, it.checked ? icon('check') : null),
        h('span', { class: 'menu-icon' }, it.icon ? icon(it.icon) : it.color ? h('span', { class: 'menu-dot', style: { background: it.color } }) : null),
        h('span', { class: 'menu-label' }, it.label),
        it.shortcut ? h('span', { class: 'menu-shortcut' }, it.shortcut) : null,
        it.children ? h('span', { class: 'menu-arrow' }, icon('chevron')) : null,
      );
      if (this.noFocus) row.addEventListener('pointerdown', (e) => e.preventDefault()); // nebrat fokus textovému poli
      row.addEventListener('click', (e) => {
        e.stopPropagation();
        if (it.disabled) return;
        if (it.children) {
          this.openSub(it, row);
          return;
        }
        IwMenu.closeAll();
        it.onSelect?.();
      });
      row.addEventListener('pointerenter', () => {
        this.focusIndex = idx;
        this.highlight();
        window.clearTimeout(this.subTimer);
        if (it.children && !it.disabled) this.openSub(it, row);
        else this.subTimer = window.setTimeout(() => this.sub?.close(), 350); // prodleva: cesta myší do podnabídky
      });
      this.appendChild(row);
    });
    this.addEventListener('keydown', (e) => this.onKey(e));
  }

  private openSub(it: MenuItem, row: HTMLElement): void {
    if (this.sub && this.sub.isConnected && this.sub.items === it.children) return;
    this.sub?.close();
    const sub = new IwMenu();
    sub.items = it.children ?? [];
    sub.parentMenu = this;
    sub.render();
    sub.addEventListener('pointerenter', () => window.clearTimeout(this.subTimer));
    document.body.appendChild(sub);
    // podnabídka vpravo od řádku; když se nevejde, vlevo od nabídky (nikdy přes ni)
    const r = row.getBoundingClientRect();
    const sr = sub.getBoundingClientRect();
    let left = r.right - 2;
    if (left + sr.width > window.innerWidth - 8) left = Math.max(4, this.getBoundingClientRect().left - sr.width + 2);
    const top = Math.max(4, Math.min(r.top - 4, window.innerHeight - sr.height - 8));
    sub.style.left = left + 'px';
    sub.style.top = top + 'px';
    this.sub = sub;
  }

  private highlight(): void {
    Array.from(this.querySelectorAll('.menu-item')).forEach((el, i) => el.classList.toggle('focus', i === this.visibleIndex(this.focusIndex)));
  }

  private visibleIndex(idx: number): number {
    let n = -1;
    for (let i = 0; i <= idx && i < this.items.length; i++) if (!this.items[i]?.separator && !this.items[i]?.header) n++;
    return n;
  }

  private onKey(e: KeyboardEvent): void {
    const enabled = this.items.map((it, i) => ({ it, i })).filter((x) => !x.it.separator && !x.it.header && !x.it.disabled);
    if (!enabled.length) return;
    const pos = enabled.findIndex((x) => x.i === this.focusIndex);
    if (e.key === 'ArrowDown') {
      this.focusIndex = enabled[(pos + 1) % enabled.length]?.i ?? 0;
      this.highlight();
      e.preventDefault();
    } else if (e.key === 'ArrowUp') {
      this.focusIndex = enabled[(pos - 1 + enabled.length) % enabled.length]?.i ?? 0;
      this.highlight();
      e.preventDefault();
    } else if (e.key === 'Enter' || e.key === ' ') {
      const it = this.items[this.focusIndex];
      if (it && !it.children) {
        IwMenu.closeAll();
        it.onSelect?.();
      }
      e.preventDefault();
    } else if (e.key === 'Escape') {
      this.close();
      e.preventDefault();
    }
  }

  close(): void {
    window.clearTimeout(this.subTimer);
    this.sub?.close();
    this.sub = null;
    if (this.parentMenu?.sub === this) this.parentMenu.sub = null;
    this.cleanup?.();
    this.cleanup = null;
    const hadFocus = this.contains(document.activeElement);
    this.remove();
    if (openMenu === this) openMenu = null;
    // fokus zpět na tlačítko / prvek, odkud se nabídka otevřela (jinak skončí na <body> a nefungují zkratky okna)
    if (hadFocus && !this.parentMenu && this.origin?.isConnected) this.origin.focus({ preventScroll: true });
  }
}

customElements.define('iw-menu', IwMenu);

/** Připojí kontextové menu k prvku (pravé tlačítko / dlouhý stisk / Shift+F10). */
export function contextMenu(el: HTMLElement, provider: (e: MouseEvent | PointerEvent) => MenuItem[] | null): void {
  let shownByTimer = false;
  el.addEventListener('contextmenu', (e) => {
    cancel();
    if (shownByTimer) {
      // nabídku už otevřel dlouhý stisk; prohlížeč posílá contextmenu o chvíli později
      shownByTimer = false;
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    const items = provider(e);
    if (!items) {
      if (e.defaultPrevented) e.stopPropagation(); // prvek stisk vyřídil sám (režim výběru) – ne nabídka plochy pod oknem
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    IwMenu.show(items, e.clientX, e.clientY);
  });
  // dlouhý stisk na dotyku. Položky s draggable by prohlížeč po dlouhém stisku začal sám přetahovat
  // (a našemu stisku poslal pointercancel), proto se jim nativní přetahování na dobu dotyku vypne –
  // myš a pero ho mají dál. Přesun na dotyku = režim výběru + Vyjmout/Vložit nebo „Přesunout do…“.
  let timer: number | undefined;
  let dragLocked: HTMLElement | null = null;
  el.addEventListener('pointerdown', (e) => {
    if (e.pointerType !== 'touch') return;
    // dlouhý stisk patří nejvnitřnějšímu prvku s nabídkou (řádek v okně), ne ploše pod oknem
    const mark = e as PointerEvent & { iwLongPress?: boolean };
    if (mark.iwLongPress) return;
    mark.iwLongPress = true;
    const drag = (e.target as HTMLElement).closest<HTMLElement>('[draggable="true"]');
    if (drag && el.contains(drag)) {
      drag.draggable = false;
      dragLocked = drag;
    }
    shownByTimer = false;
    timer = window.setTimeout(() => {
      const items = provider(e);
      if (items) {
        shownByTimer = true;
        IwMenu.show(items, e.clientX, e.clientY);
        window.setTimeout(() => { shownByTimer = false; }, 1200);
      }
    }, 500);
  });
  const unlock = () => {
    if (dragLocked) {
      const d = dragLocked;
      dragLocked = null;
      window.setTimeout(() => { d.draggable = true; }, 0);
    }
  };
  const cancel = () => window.clearTimeout(timer);
  el.addEventListener('pointerup', () => { cancel(); unlock(); });
  el.addEventListener('pointermove', cancel);
  el.addEventListener('pointercancel', () => { cancel(); unlock(); });
}
